/**
 * CloudManager - Handles procedural cloud generation and animation
 *
 * Clouds are rendered as sprites with procedurally generated textures.
 * They're distributed ahead of the camera and recycled as the player
 * moves forward. Cloud colors change with time of day.
 *
 * Responsibilities:
 * - Generate procedural cloud textures using noise
 * - Create and position cloud sprites
 * - Recycle clouds as player moves (dispose behind, create ahead)
 * - Handle fade-in effect for distant clouds
 * - Update cloud colors during day/night transitions
 */

import * as THREE from 'three';
import { SimplexNoise } from '../utils/SimplexNoise.js';
import { CLOUDS } from '../config/constants.js';

export class CloudManager {
    /**
     * Create a new CloudManager
     * @param {THREE.Scene} scene - The Three.js scene to add clouds to
     * @param {THREE.Camera} camera - The camera to follow
     * @param {WorldTranslator} worldTranslator - The world translation system
     * @param {Object} options - Additional options
     * @param {number} options.fogFar - Far fog distance (for spawn positioning)
     * @param {boolean} options.isMobile - Whether running on mobile (reduced settings)
     */
    constructor(scene, camera, worldTranslator, options = {}, preloader = null) {
        this.scene = scene;
        this.camera = camera;
        this.worldTranslator = worldTranslator;
        this.fogFar = options.fogFar || 5000;
        this.isMobile = options.isMobile || false;

        // Self-register for preload tracking with dispose callback
        this._onPreloadComplete = preloader?.register('clouds', {
            dispose: () => this.dispose()
        });

        // Noise generator for procedural textures
        this.noise = new SimplexNoise();

        // Cloud sprite array
        this.clouds = [];
        this.seedCounter = 0;

        // Configuration (from constants, reduced on mobile)
        this.enabled = CLOUDS.ENABLED;
        this.count = this.isMobile ? CLOUDS.COUNT_MOBILE : CLOUDS.COUNT;
        this.minHeight = CLOUDS.MIN_HEIGHT;
        this.maxHeight = CLOUDS.MAX_HEIGHT;
        this.minDistance = CLOUDS.MIN_DISTANCE;
        this.maxDistance = this.isMobile ? CLOUDS.MAX_DISTANCE_MOBILE : CLOUDS.MAX_DISTANCE;
        this.minSize = CLOUDS.MIN_SIZE;
        this.maxSize = CLOUDS.MAX_SIZE;
        this.textureSize = CLOUDS.TEXTURE_SIZE;
        this.defaultOpacity = CLOUDS.DEFAULT_OPACITY;
        this.fadeThreshold = CLOUDS.FADE_THRESHOLD;
        this.fadeInDistance = this.isMobile ? CLOUDS.FADE_IN_DISTANCE_MOBILE : CLOUDS.FADE_IN_DISTANCE;

        // Recycling distances
        this.recycleDistance = 200;      // Distance behind camera to recycle
        // Spawn clouds beyond fade distance so they always start invisible
        // But keep within sky sphere (radius ~4500) so they don't pop through
        this.spawnDistance = this.fadeInDistance + 200;

        // Current color palette (will be set externally)
        this.currentPaletteIndex = 0;
        this.colorPalettes = [];

        // Texture cache (Phase 3.2 optimization)
        this._textureCache = [];
        this._textureCacheSize = this.isMobile ? 10 : 20; // Number of unique textures to pre-generate

        // Current opacity (may be interpolated during transitions)
        this.currentOpacity = this.defaultOpacity;

        // Disposal state
        this._disposed = false;
    }

    /**
     * Set color palettes for cloud tinting
     * @param {Array} palettes - Array of color palette objects
     */
    setColorPalettes(palettes) {
        this.colorPalettes = palettes;
    }

    /**
     * Set current palette index for cloud colors
     * @param {number} index - Palette index
     */
    setPaletteIndex(index) {
        this.currentPaletteIndex = index;
    }

    /**
     * Set cloud opacity (used during day/night transitions)
     * @param {number} opacity - Target opacity (0-1)
     */
    setOpacity(opacity) {
        this.currentOpacity = opacity;
    }

    /**
     * Initialize clouds
     * Call this after scene and camera are ready
     */
    initialize() {
        if (!this.enabled) {
            this._onPreloadComplete?.();
            return;
        }
        this.createInitialClouds();
        this._onPreloadComplete?.();
    }

    /**
     * Get a texture from cache, or generate and cache if needed
     * @returns {THREE.CanvasTexture} A cached texture
     * @private
     */
    _getCachedTexture() {
        // Pre-populate cache if needed
        while (this._textureCache.length < this._textureCacheSize) {
            const texture = this._generateTextureInternal(this._textureCache.length);
            this._textureCache.push(texture);
        }
        // Return random texture from cache
        return this._textureCache[Math.floor(Math.random() * this._textureCache.length)];
    }

    /**
     * Generate a procedural cloud texture using noise
     * @param {number} seed - Seed for unique texture generation
     * @returns {THREE.CanvasTexture} The generated texture
     * @private
     */
    _generateTextureInternal(seed) {
        const size = this.textureSize;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');

        const imageData = ctx.createImageData(size, size);
        const data = imageData.data;

        // Use seed to create unique noise offset for this cloud
        const offsetX = seed * 1000;
        const offsetY = seed * 2000;

        // Generate cloud using multi-octave noise
        for (let y = 0; y < size; y++) {
            for (let x = 0; x < size; x++) {
                const idx = (y * size + x) * 4;

                // Normalized coordinates (-1 to 1)
                const nx = (x / size - 0.5) * 2;
                const ny = (y / size - 0.5) * 2;

                // Distance from center (for circular falloff)
                const dist = Math.sqrt(nx * nx + ny * ny);
                const falloff = Math.max(0, 1 - dist);

                // Multi-octave noise for cloud detail
                const scale1 = 0.02;
                const scale2 = 0.05;
                const scale3 = 0.1;

                const noise1 = this.noise.noise((x + offsetX) * scale1, (y + offsetY) * scale1);
                const noise2 = this.noise.noise((x + offsetX) * scale2, (y + offsetY) * scale2);
                const noise3 = this.noise.noise((x + offsetX) * scale3, (y + offsetY) * scale3);

                // Combine noise layers
                const noiseValue = (noise1 * 0.5 + noise2 * 0.3 + noise3 * 0.2);

                // Apply falloff and threshold for cloud shape
                let alpha = (noiseValue + 0.3) * falloff;
                alpha = Math.max(0, Math.min(1, alpha));

                // Soften edges
                alpha = Math.pow(alpha, 1.5);

                // Set RGB to mid-gray so color tinting shows through
                const gray = 200;
                data[idx] = gray;     // R
                data[idx + 1] = gray; // G
                data[idx + 2] = gray; // B
                data[idx + 3] = alpha * 255; // A
            }
        }

        ctx.putImageData(imageData, 0, 0);

        const texture = new THREE.CanvasTexture(canvas);
        texture.needsUpdate = true;

        return texture;
    }

    /**
     * Create a single cloud sprite
     * @param {number} zPosition - Z position for the cloud
     * @returns {THREE.Sprite} The created cloud sprite
     * @private
     */
    createCloud(zPosition) {
        // Get texture from cache (Phase 3.2 optimization)
        const texture = this._getCachedTexture();

        // Random size
        const size = this.minSize + Math.random() * (this.maxSize - this.minSize);

        // Pick a random cloud color from current palette
        let cloudColor = 0xFFFFFF;
        if (this.colorPalettes.length > 0) {
            const palette = this.colorPalettes[this.currentPaletteIndex];
            if (palette && palette.cloudColors && palette.cloudColors.length > 0) {
                cloudColor = palette.cloudColors[Math.floor(Math.random() * palette.cloudColors.length)];
            }
        }

        // Create sprite material
        const material = new THREE.SpriteMaterial({
            map: texture,
            color: cloudColor,
            transparent: true,
            opacity: 0, // Start at 0 for fade-in
            fog: false,
            depthWrite: false
        });

        const sprite = new THREE.Sprite(material);
        sprite.scale.set(size, size * 0.6, 1); // Slightly flattened clouds
        sprite.renderOrder = 100; // Render clouds after terrain

        // Position cloud
        // Random X position weighted toward center (exponent > 1 biases toward 0)
        const lateralRange = Math.pow(Math.random(), 1.5);
        const lateralSign = Math.random() < 0.5 ? -1 : 1;
        const x = lateralSign * lateralRange * this.maxDistance;

        // Random height
        const y = this.minHeight + Math.random() * (this.maxHeight - this.minHeight);

        sprite.position.set(x, y, zPosition);

        // Store color data for transitions
        sprite.userData.currentColor = cloudColor;
        sprite.userData.nextColor = null;

        this.worldTranslator.add(sprite);
        return sprite;
    }

    /**
     * Create initial cloud distribution
     * @private
     */
    createInitialClouds() {
        // In world translation model, clouds use local Z coordinates
        // Negative Z = ahead, positive Z = behind camera
        // Distribute clouds from slightly behind to far ahead
        const spawnRangeStart = 500; // Some clouds slightly behind
        const spawnRangeEnd = -this.spawnDistance;

        // Get current groupOffset (should be 0 at initialization)
        const groupOffset = this.worldTranslator ? this.worldTranslator.getGroupOffset() : 0;

        for (let i = 0; i < this.count; i++) {
            // Bias distribution toward ahead of camera using power curve
            const t = Math.pow(i / this.count, 0.7); // More clouds ahead
            const zPos = spawnRangeStart + (spawnRangeEnd - spawnRangeStart) * t;
            const cloud = this.createCloud(zPos);

            // Set initial opacity based on visual distance (skip the slow fade-in for initial clouds)
            const visualZ = zPos + groupOffset;
            const distanceFromCamera = -visualZ;

            // Use same fade logic as update()
            const fadeStart = this.fadeInDistance;
            const fadeEnd = this.fadeInDistance * 0.5;
            let fadeInProgress;
            if (distanceFromCamera >= fadeStart) {
                fadeInProgress = 0;
            } else if (distanceFromCamera <= fadeEnd) {
                fadeInProgress = 1;
            } else {
                fadeInProgress = 1 - (distanceFromCamera - fadeEnd) / (fadeStart - fadeEnd);
                fadeInProgress = fadeInProgress * fadeInProgress * (3 - 2 * fadeInProgress);
            }
            cloud.material.opacity = this.currentOpacity * fadeInProgress;

            this.clouds.push(cloud);
        }
    }

    /**
     * Update cloud colors for all clouds (used during palette transitions)
     * @param {number} currentColor - Current color (interpolated)
     * @param {number} nextColor - Next color (target)
     * @param {number} t - Transition progress (0-1)
     */
    updateColors(currentColor, nextColor, t) {
        for (const cloud of this.clouds) {
            if (t > 0 && nextColor !== null && currentColor !== null) {
                const color = this.lerpColor(currentColor, nextColor, t);
                cloud.material.color.setHex(color);
            } else if (currentColor !== null) {
                cloud.material.color.setHex(currentColor);
            }
        }
    }

    /**
     * Update clouds each frame
     * Handles recycling behind camera and opacity fading
     */
    update() {
        if (this._disposed || !this.enabled || this.clouds.length === 0) return;

        // Recycle clouds that are behind the camera
        // In world translation model, positive local Z = behind camera
        const groupOffset = this.worldTranslator.getGroupOffset();
        for (let i = this.clouds.length - 1; i >= 0; i--) {
            const cloud = this.clouds[i];
            const visualZ = cloud.position.z + groupOffset;

            if (this.worldTranslator.isPastCamera(cloud.position.z, this.recycleDistance)) {
                // Remove and dispose (but NOT texture - it's cached)
                this.worldTranslator.remove(cloud);
                // Don't dispose texture: cloud.material.map.dispose();
                cloud.material.dispose();
                this.clouds.splice(i, 1);

                // Create new cloud ahead of camera in VISUAL space
                // Visual Z = local Z + groupOffset, so local Z = visual Z - groupOffset
                // We want visual Z to be negative (ahead), so spawn at -spawnDistance visual
                const targetVisualZ = -this.spawnDistance - Math.random() * 1000;
                const newLocalZ = targetVisualZ - groupOffset;
                const newCloud = this.createCloud(newLocalZ);
                this.clouds.push(newCloud);
            }
        }

        // Update opacity with distance-based fade-in
        // Use visual distance (local Z + groupOffset) for proper fade calculation
        for (const cloud of this.clouds) {
            const visualZ = cloud.position.z + groupOffset;
            // visualZ is negative when ahead of camera, positive when behind
            // distanceFromCamera should be positive for clouds ahead
            const distanceFromCamera = -visualZ;

            // Clouds fade in as they get closer
            // Start fading at fadeInDistance, fully visible at fadeInDistance * 0.5
            const fadeStart = this.fadeInDistance;
            const fadeEnd = this.fadeInDistance * 0.5;
            let fadeInProgress;

            if (distanceFromCamera >= fadeStart) {
                fadeInProgress = 0; // Too far, invisible
            } else if (distanceFromCamera <= fadeEnd) {
                fadeInProgress = 1; // Close enough, fully visible
            } else {
                // Smooth fade between fadeStart and fadeEnd
                fadeInProgress = 1 - (distanceFromCamera - fadeEnd) / (fadeStart - fadeEnd);
                // Apply smoothstep for even smoother transition
                fadeInProgress = fadeInProgress * fadeInProgress * (3 - 2 * fadeInProgress);
            }

            const targetOpacity = this.currentOpacity * fadeInProgress;

            // Faster interpolation for smoother fade-in (0.1 instead of 0.05)
            const current = cloud.material.opacity;
            cloud.material.opacity = current + (targetOpacity - current) * 0.1;
        }
    }

    /**
     * Interpolate between two hex colors
     * @param {number} color1 - First color
     * @param {number} color2 - Second color
     * @param {number} t - Interpolation factor (0-1)
     * @returns {number} Interpolated hex color
     * @private
     */
    lerpColor(color1, color2, t) {
        const r1 = (color1 >> 16) & 0xff;
        const g1 = (color1 >> 8) & 0xff;
        const b1 = color1 & 0xff;

        const r2 = (color2 >> 16) & 0xff;
        const g2 = (color2 >> 8) & 0xff;
        const b2 = color2 & 0xff;

        const r = Math.round(r1 + (r2 - r1) * t);
        const g = Math.round(g1 + (g2 - g1) * t);
        const b = Math.round(b1 + (b2 - b1) * t);

        return (r << 16) | (g << 8) | b;
    }

    /**
     * Get the clouds array (for external color updates)
     * @returns {Array} Array of cloud sprites
     */
    getClouds() {
        return this.clouds;
    }

    /**
     * Clean up all resources
     */
    dispose() {
        if (this._disposed) return;
        this._disposed = true;

        // Dispose cloud materials (but NOT textures - they're in the cache)
        for (const cloud of this.clouds) {
            this.worldTranslator.remove(cloud);
            cloud.material.dispose();
        }
        this.clouds = [];

        // Dispose cached textures
        for (const texture of this._textureCache) {
            texture.dispose();
        }
        this._textureCache = [];
    }
}
